import React from 'react';
import {
  AppBar,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  IconButton,
  Table,
  TableBody,
  TableContainer,
  Toolbar,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { Close } from '@material-ui/icons';
import Excel from 'assets/icons/svg/Excel';
import { GoldCupIcon, SilverCupIcon, BronzeCupIcon, MedalIcon } from '../../../assets/icons';
import { IPartnerRatingByPartnerUgo } from '../../../interfaces';
import { INomenclature } from '../interfaces';
import PartnerRatingTableRow from './PartnerRatingTableRow';

interface IProps extends Omit<DialogProps, 'onClose'> {
  nomenclature: INomenclature;
  year: number;
  partnersCount: number;
  partnerRating: IPartnerRatingByPartnerUgo;
  data: IPartnerRatingByPartnerUgo;
  loading: boolean;
  success: boolean;
  error: string | null;
  exportExcelLoading: boolean;
  onExportExcel(): void;
  onClose(): void;
}

const PartnerRatingDialog = ({
  open,
  nomenclature,
  year,
  partnersCount,
  partnerRating,
  data,
  loading,
  success,
  error,
  exportExcelLoading,
  onExportExcel,
  onClose,
  ...props
}: IProps) => {
  return (
    <Dialog {...props} open={open} onClose={onClose} maxWidth="xl" fullWidth scroll="paper">
      <AppBar style={{ position: 'relative' }}>
        <Box component={Toolbar} sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography component="h3" variant="h5">
            <b>Рейтинг партнеров</b>
          </Typography>

          <IconButton aria-label="Закрыть" color="inherit" onClick={onClose}>
            <Close color="inherit" />
          </IconButton>
        </Box>
      </AppBar>

      <DialogContent>
        {loading ? (
          <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : Boolean(error) ? (
          <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
            <Typography component="h3" variant="h4" align="center">
              {error}
            </Typography>
          </Box>
        ) : (
          success && (
            <>
              {Boolean(data) ? (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography style={{ maxWidth: '70%' }} component="h2" variant="h3">
                        <b>{partnerRating.smrPartnerName}</b>
                      </Typography>

                      <Typography component="p" variant="h6">
                        БИН: {data.partnerBin}
                      </Typography>

                      <Typography component="p" variant="h6">
                        {data.partnerDirector}
                      </Typography>

                      <Typography component="p" variant="h6">
                        {data.partnerPhone}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', gridGap: 16 }}>
                      <Box>
                        <Typography component="h3" variant="h3" noWrap>
                          <b>{partnerRating.ratingPosition} место</b>
                        </Typography>

                        <Typography component="h4" variant="h6">
                          Всего партнеров {partnersCount}
                        </Typography>
                      </Box>

                      {[1, 2, 3].some((ratingPosition) => ratingPosition === partnerRating.ratingPosition) ? (
                        partnerRating.ratingPosition === 1 ? (
                          <GoldCupIcon />
                        ) : partnerRating.ratingPosition === 2 ? (
                          <SilverCupIcon />
                        ) : (
                          <BronzeCupIcon />
                        )
                      ) : [4, 5, 6, 7, 8, 9, 10].some(
                          (ratingPosition) => ratingPosition === partnerRating.ratingPosition,
                        ) ? (
                        <MedalIcon />
                      ) : null}
                    </Box>
                  </Box>

                  <TableContainer style={{ width: 'calc(100% + 48px)', margin: '0 -24px' }}>
                    <Table size="small" style={{ tableLayout: 'fixed' }}>
                      <TableBody>
                        {data.realEstates.map((realEstateRating, index) => (
                          <PartnerRatingTableRow
                            key={realEstateRating.realEstateGuid}
                            year={year}
                            partnerRating={data}
                            realEstateRating={realEstateRating}
                            nomenclature={nomenclature}
                            rowIndex={index}
                          />
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              ) : (
                <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
                  <Typography component="h3" variant="h4" align="center">
                    Данные отсутствуют
                  </Typography>
                </Box>
              )}
            </>
          )
        )}
      </DialogContent>

      {Boolean(data) && (
        <DialogActions>
          <Box sx={{ p: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
            <Tooltip title="Скачать отчет в Excel">
              <span>
                <IconButton
                  aria-label="Скачать отчет в Excel"
                  onClick={onExportExcel}
                  disabled={loading || exportExcelLoading}
                >
                  <Excel style={{ color: '#3C763D' }} />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default PartnerRatingDialog;
